import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Flex, Modal, Typography, theme } from 'antd';
import { LockOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { useToken } = theme;

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 30;

interface OtpModalProps {
  visible: boolean;
  onCancel: () => void;
  onVerify: (otp: string) => Promise<void>;
  modalTitle?: string;
  closable?: boolean;
  handleOtpResend?: () => void;
  resend?: boolean;
}

const OTPModal: React.FC<OtpModalProps> = ({
  visible,
  onCancel,
  onVerify,
  modalTitle = 'OTP Verification',
  closable = true,
  handleOtpResend,
  resend = true,
}) => {
  const { token } = useToken();
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(RESEND_COOLDOWN);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setDigits(Array(OTP_LENGTH).fill(''));
      setError(null);
      setLoading(false);
      setCountdown(RESEND_COOLDOWN);
      setIsResendDisabled(true);
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [visible]);

  // Countdown timer
  useEffect(() => {
    if (!visible || !isResendDisabled) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [visible, isResendDisabled]);

  const otp = digits.join('');

  const handleVerify = useCallback(async () => {
    if (otp.length !== OTP_LENGTH) {
      setError('Please enter all 6 digits.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onVerify(otp);
      setDigits(Array(OTP_LENGTH).fill(''));
    } catch {
      setError('Invalid OTP. Please try again.');
      setDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  }, [otp, onVerify]);

  const handleResend = () => {
    if (handleOtpResend) {
      handleOtpResend();
      setCountdown(RESEND_COOLDOWN);
      setIsResendDisabled(true);
      setDigits(Array(OTP_LENGTH).fill(''));
      setError(null);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setError(null);

    // Auto-advance to next input
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && otp.length === OTP_LENGTH && !loading) {
      handleVerify();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    const newDigits = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      newDigits[i] = char;
    });
    setDigits(newDigits);
    setError(null);
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <Modal
      title={null}
      open={visible}
      closable={closable}
      onCancel={onCancel}
      maskClosable={closable}
      keyboard={closable}
      footer={null}
      centered
      width={420}
      styles={{ body: { padding: '8px 0 0' } }}
    >
      <Flex
        vertical
        align="center"
        gap={4}
        style={{ textAlign: 'center', padding: '8px 0 16px' }}
      >
        {/* Icon */}
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: token.colorPrimaryBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 8,
          }}
        >
          <LockOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
        </div>

        <Title level={4} style={{ margin: 0 }}>
          {modalTitle}
        </Title>
        <Text type="secondary" style={{ fontSize: 13, maxWidth: 300 }}>
          We sent a verification code to your registered email or phone. Enter
          it below to continue.
        </Text>
      </Flex>

      {/* OTP Inputs */}
      <Flex
        justify="center"
        gap={8}
        style={{ margin: '20px 0 8px' }}
        onPaste={handlePaste}
      >
        {digits.map((digit, i) => (
          <input
            key={i}
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
            disabled={loading}
            style={{
              width: 48,
              height: 56,
              textAlign: 'center',
              fontSize: 22,
              fontWeight: 600,
              border: `1.5px solid ${
                error
                  ? token.colorError
                  : digit
                    ? token.colorPrimary
                    : token.colorBorderSecondary
              }`,
              borderRadius: 10,
              outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s',
              background: loading
                ? token.colorBgContainerDisabled
                : token.colorBgContainer,
              caretColor: token.colorPrimary,
            }}
          />
        ))}
      </Flex>

      {/* Error */}
      {error && (
        <Text
          type="danger"
          style={{
            display: 'block',
            textAlign: 'center',
            fontSize: 13,
            marginTop: 4,
          }}
        >
          {error}
        </Text>
      )}

      {/* Verify Button */}
      <Button
        type="primary"
        size="large"
        block
        onClick={handleVerify}
        loading={loading}
        disabled={loading || otp.length !== OTP_LENGTH}
        style={{ marginTop: 20, height: 44, fontWeight: 500 }}
      >
        {loading ? 'Verifying...' : 'Verify'}
      </Button>

      {/* Resend */}
      {resend && (
        <Flex justify="center" align="center" gap={4} style={{ marginTop: 16 }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Didn&apos;t receive a code?
          </Text>
          {isResendDisabled ? (
            <Text style={{ fontSize: 13, color: token.colorTextDisabled }}>
              Resend in {countdown}s
            </Text>
          ) : (
            <Button
              type="link"
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleResend}
              style={{ padding: 0, fontSize: 13 }}
            >
              Resend
            </Button>
          )}
        </Flex>
      )}
    </Modal>
  );
};

export default OTPModal;
